import PolarisIcon from '@/components/_common/PolarisIcon';
import { FormHelperText, Grid } from '@mui/material';
import DateTimeDisplay from '../../../_common/DateTimeDisplay';
import DeleteRowButton from '../../../_common/DeleteRowButton';
import { useConfigValue } from '../../../_hooks/useConfigValue';
import { JobDetail } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';

type ProfileDetailSummaryProps = {
  currentJob: JobDetail;
  deleteProfile: () => void;
  allowDeletion?: boolean;
};

const ProfileDetailSummary = ({ currentJob, deleteProfile, allowDeletion = true }: ProfileDetailSummaryProps) => {
  const disableDueDate = useConfigValue(['orderPlacementConfig', 'disableDueDate']);
  const disableEmbargoDate = useConfigValue(['orderPlacementConfig', 'disableEmbargoDate']);

  return (
    <Wrapper>
      <Grid container>
        <Grid item sm={4}>
          <AccordionProfileSectionDiv>
            <SummaryPolarisIcon view={'accordion-profile-text'} width={22} icon={currentJob.profile?.workflow_type || 'version_based'} />
            <AccordionProfileInfoDiv>
              <AccordionProfileTextSpan>{currentJob.profile?.name}</AccordionProfileTextSpan>
              <span>
                Customer: <strong>{currentJob.customer}</strong>
              </span>
            </AccordionProfileInfoDiv>
          </AccordionProfileSectionDiv>
        </Grid>
        <Grid item md={4}>
          <AccordionProfileSectionDiv>
            <SummaryPolarisIcon view={'accordion-due-date-text'} width={22} icon="calendar" />
            <AccordionProfileInfoDiv>
              <AccordionDueDateTextSpan>Due Date</AccordionDueDateTextSpan>
              {disableDueDate ? (
                <FormHelperText sx={{ lineHeight: 1, m: 0 }}>Externally managed</FormHelperText>
              ) : (
                <DateTimeDisplay singleLine>{currentJob.due_date}</DateTimeDisplay>
              )}
            </AccordionProfileInfoDiv>
          </AccordionProfileSectionDiv>
        </Grid>
        {!disableEmbargoDate && (
          <Grid item md={4}>
            <AccordionProfileSectionDiv>
              <SummaryPolarisIcon view={'accordion-embargo-date-text'} width={22} icon="calendar_clock" />
              <AccordionProfileInfoDiv>
                <AccordionEmbargoDateTextSpan>Embargo Date</AccordionEmbargoDateTextSpan>
                <DateTimeDisplay singleLine missingText="-">
                  {currentJob.embargo_date}
                </DateTimeDisplay>
              </AccordionProfileInfoDiv>
            </AccordionProfileSectionDiv>
          </Grid>
        )}
      </Grid>
      {allowDeletion && (
        <DeleteRow>
          <DeleteRowButton onClick={deleteProfile} />
        </DeleteRow>
      )}
    </Wrapper>
  );
};

export default ProfileDetailSummary;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(1, 0)};
`;

const AccordionProfileSectionDiv = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: 15px;
`;

const AccordionProfileInfoDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 10px;
`;

const SummaryPolarisIcon = styled(PolarisIcon)<{ view: string }>`
  margin-top: 5px;

  ${({ view, theme }) => {
    switch (view) {
      case 'accordion-profile-text':
        return `
          color: ${theme.palette.teal?.main};
        `;
      case 'accordion-due-date-text':
        return `
          color: ${theme.palette.info?.main};
          margin-bottom: ${theme.spacing(0.5)};
        `;
      case 'accordion-embargo-date-text':
        return `
          color: ${theme.palette.default.main};
          margin-bottom: ${theme.spacing(0.5)};
        `;
      default:
        return '';
    }
  }}
`;

const AccordionProfileTextSpan = styled.span`
  color: ${({ theme }) => theme.palette.teal?.main};
  font-size: 1.2rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const AccordionDueDateTextSpan = styled.span`
  color: ${({ theme }) => theme.palette.info?.main};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  font-size: 1.2rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const AccordionEmbargoDateTextSpan = styled.span`
  color: ${({ theme }) => theme.palette.default.main};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  font-size: 1.2rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const DeleteRow = styled.div`
  position: absolute;
  right: ${(props) => props.theme.spacing(3)};
  top: ${(props) => props.theme.spacing(3)};
`;
